import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnInit, Output } from '@angular/core';
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import { Draw, Modify, Snap } from 'ol/interaction.js';
import { OSM, Vector as VectorSource } from 'ol/source.js';
import { Tile, Vector as VectorLayer } from 'ol/layer.js';
import { AlertComponent } from '../alert/alert.component';
import { FullScreen, defaults as defaultControls, Control } from 'ol/control';
import { ApplyControls } from './controls/apply.controls';
import { DrawControls } from './controls/draw.controls';
import { DrawEvent } from 'ol/interaction/Draw';
import { GeoJSON } from 'ol/format';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Feature, MapBrowserEvent } from 'ol';
import { Polygon } from 'ol/geom';
import { SearchControls } from './controls/search.controls';
import { MapSearchResponseItem, OpenStreetApi } from '../../services/open-street.api';
import { SearchResultControls } from './controls/search-result.controls';
import { fromLonLat } from 'ol/proj';
import { Type } from 'ol/geom/Geometry';
import { AttachmentAttachmentRead } from '../../openapi';
import { SpinnerComponent } from '../spinner/spinner.component';
import { NavOption } from './controls/quick-nav.controls';


export interface DrawEventResult {
  type: string;
  crs: {
    type: 'name',
    properties: { name: 'urn:ogc:def:crs:EPSG::3857' }
  };
  geometry: {
    type: string;
    coordinates: number[][][];
  };
}


@Component({
  selector: 'app-draw-and-modify-terrain',
  standalone: true,
  imports: [
    AlertComponent,
    CommonModule,
    SpinnerComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DrawAndModifyTerrainComponent),
      multi: true
    },
    OpenStreetApi
  ],
  templateUrl: './draw-and-modify-terrain.component.html'
})
export class DrawAndModifyTerrainComponent implements OnInit, ControlValueAccessor {

  @Input()
  public readonly = false;

  @Input()
  public messageContent: string = 'Wyrysuj obszar następnie kliknij zatwierdź';

  private geoJSON: DrawEventResult[] = [];

  private geoJSONParser = new GeoJSON();

  private vectorStyle = {
    'fill-color': 'rgba(255, 255, 255, 0.2)',
    'stroke-color': '#df0000',
    'stroke-width': 2,
    'circle-radius': 6,
    'circle-fill-color': '#df0000',
    'circle-fill-opacity': 0.2
  };

  public onChange: any = () => {
  };

  public onTouch: any = () => {
  };

  public disabled = false;

  @Output()
  public cancel = new EventEmitter();

  @Output()
  public apply = new EventEmitter();

  @Output()
  public mapImage = new EventEmitter<AttachmentAttachmentRead | null>();

  @Input()
  public drawType: Type = 'Polygon';

  @Input()
  public createImage = false;

  public pending = false;

  private raster: Tile = new Tile({
    source: new OSM()
  });

  private source: VectorSource = new VectorSource();

  private draw: Draw | undefined;

  private snap = new Snap({
    source: this.source
  });

  private vector = new VectorLayer({
    source: this.source,
    style: this.vectorStyle
  });

  private fullScreenControls: FullScreen = new FullScreen();

  private applyControls: ApplyControls = new ApplyControls({
    onApply: () => {
      if (this.createImage) {
        this.pending = true;
        this.openStreetApi.createMapImage(this.map as Map).subscribe({
          next: attachment => {
            this.apply.emit(this.geoJSON);
            this.onChange(this.geoJSON);
            this.mapImage.emit(attachment);
            this.pending = false;
          },
          error: () => {
            this.onChange([]);
            this.apply.emit([]);
            this.pending = false;
            this.mapImage.emit(null);
          }
        });
      } else {
        this.apply.emit(this.geoJSON);
        this.onChange(this.geoJSON);
      }
    },
    onCancel: () => {
      this.cancel.emit();
    }
  });

  private drawControls: DrawControls = new DrawControls({
    onRollback: () => {
      this.draw?.removeLastPoint();
    },
    onCancelDraw: () => {
      this.draw?.abortDrawing();
    },
    onClear: () => {
      this.source.getFeatures().forEach(feature => {
        this.source.removeFeature(feature);
      });
      if (this.prevLayer) {
        this.map?.removeLayer(this.prevLayer);
      }
      this.geoJSON = [];
      this.onChange(this.geoJSON);
    }
  });

  private searchControls: SearchControls = new SearchControls({
    onSearch: (v: string) => {
      this.openStreetApi.search(v).toPromise().then(v => {
        if (this.searchResultControls) {
          this.map?.removeControl(this.searchResultControls);
        }
        this.searchResultControls = new SearchResultControls({
          data: v || [],
          onItemClick: (v: MapSearchResponseItem | null) => {
            if (v) {
              this.centerOn(v);
            }
          },
          onClose: (v: Event) => {
            if (this.searchResultControls) {
              this.map?.removeControl(this.searchResultControls);
            }
          }
        });
        this.map?.addControl(this.searchResultControls);
      });
    }
  });

  private searchResultControls: SearchResultControls | null = null;

  private map: Map | undefined;

  private prevLayer: VectorLayer | null = null;

  constructor(
    private elRef: ElementRef,
    private openStreetApi: OpenStreetApi
  ) {
  }

  private centerOn(v: NavOption | MapSearchResponseItem): void {
    this.map?.getView()?.setCenter(fromLonLat([+v.lon, +v.lat]));
    this.map?.getView()?.setZoom(18);
  }

  public getInvalid(): boolean {
    const list: DOMTokenList = this.elRef.nativeElement.classList;
    return list.contains('ng-invalid')
      && list.contains('ng-touched');
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (this.readonly) {
      this.map?.removeControl(this.searchControls as Control);
      this.map?.removeControl(this.drawControls as Control);
      this.map?.removeControl(this.applyControls as Control);
      return;
    }
    if (isDisabled) {
      this.map?.removeControl(this.searchControls as Control);
      this.map?.removeControl(this.drawControls as Control);
      this.map?.removeControl(this.applyControls as Control);
    } else {
      this.map?.addControl(this.drawControls as Control);
      this.map?.addControl(this.applyControls as Control);
      this.map?.addControl(this.searchControls as Control);
    }
    this.disabled = isDisabled;
  }

  public writeValue(data: DrawEventResult[]): void {
    if (!!data?.length) {
      this.geoJSON = data;
      this.prevLayer = new VectorLayer({
        source: new VectorSource({
          features: data.map(v => new Feature({
            geometry: new Polygon(v.geometry.coordinates as any, 'XY')
          }))
        }),
        style: this.vectorStyle
      });
      this.map?.addLayer(this.prevLayer);
    }
  }

  private createMap(): void {
    this.draw = new Draw({
      source: this.source,
      type: this.drawType,
      finishCondition: this.drawType === 'Point' ? (e: MapBrowserEvent<any>) => {
        this.vector.getSource()?.clear();
        return true;
      } : undefined
    });
    this.map = new Map({
      controls: defaultControls().extend([
        this.searchControls,
        this.fullScreenControls,
        this.drawControls,
        this.applyControls
      ]),
      layers: [this.raster, this.vector],
      target: 'map',
      view: new View({
        center: fromLonLat([17.200770, 51.17675]),
        zoom: 15
      })
    });

    const modify = new Modify({ source: this.source });
    this.map.addInteraction(this.draw);
    this.map.addInteraction(this.snap);
    this.map.addInteraction(modify);
  }

  @HostListener('document:keydown', ['$event'])
  private handleKeyboardEvents(event: KeyboardEvent) {
    switch (true) {
      // escape event
      case event.keyCode === 27: {
        this.draw?.abortDrawing();
        break;
      }
      // ctrl + z
      case event.ctrlKey && event.keyCode === 90: {
        this.draw?.removeLastPoint();
        break;
      }
      // ctrl + x
      case event.ctrlKey && event.keyCode === 88: {
        this.source.getFeatures().forEach(feature => {
          this.source.removeFeature(feature);
        });
        this.geoJSON = [];
        break;
      }
    }
  }

  private handleDrawChange(): void {
    this.draw?.on('drawend', (e: DrawEvent) => {
      let v = JSON.parse(this.geoJSONParser.writeFeature(e.feature));
      v['crs'] = { 'type': 'name', 'properties': { 'name': 'urn:ogc:def:crs:EPSG::3857' } };
      this.geoJSON.push(v);
    });
  }

  public ngOnInit(): void {
    this.createMap();
    this.handleDrawChange();
  }
}
